import {
	type RecurringExpenseDto,
	RecurringFrequency,
} from "@pilatestime/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MoreHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchExpenseCategoriesQueryOptions,
	fetchRecurringExpensesQueryOptions,
} from "@/api/orgMgmtCostManagement";
import { formatCurrency } from "@/components/billing/CurrencyDisplay";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useArchiveRecurringExpense } from "@/hooks/useCostManagementMutations";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useCategoryNameResolver } from "@/lib/expense-category-name";
import { OrganizationManagementNavigation } from "@/routes/_auth/app/_app/org/$orgId/org-management/-components/OrganizationManagementNavigation";
import { CostManagementSubNav } from "../-components/CostManagementSubNav";
import { RecurringExpenseFormDialog } from "../-components/RecurringExpenseFormDialog";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/org-management/cost-management/recurring/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;
		context.queryClient.ensureQueryData(
			fetchRecurringExpensesQueryOptions(orgId, token),
		);
		context.queryClient.ensureQueryData(
			fetchExpenseCategoriesQueryOptions(orgId, token),
		);
		return { userId, token, orgId };
	},
	component: RecurringPage,
});

function RecurringPage() {
	const { t } = useTranslation();
	const { token, orgId } = Route.useLoaderData();
	const { locale } = useDateFormatter();

	const recurringQuery = useQuery(
		fetchRecurringExpensesQueryOptions(orgId, token),
	);
	const categoriesQuery = useQuery(
		fetchExpenseCategoriesQueryOptions(orgId, token),
	);
	const archiveMutation = useArchiveRecurringExpense();
	const resolveCategoryName = useCategoryNameResolver(orgId, token);

	const [createOpen, setCreateOpen] = useState(false);
	const [editTarget, setEditTarget] = useState<
		RecurringExpenseDto | undefined
	>();

	const formatDateString = (iso: string) =>
		new Date(`${iso}T00:00:00Z`).toLocaleDateString(locale, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			timeZone: "UTC",
		});

	const next30DaysSummary = useMemo(() => {
		const rows = recurringQuery.data ?? [];
		if (rows.length === 0) return null;
		const total = projectedNext30DaysTotal(rows);
		const currency = rows[0]?.currency ?? "BRL";
		return { total, currency };
	}, [recurringQuery.data]);

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgManagement.costManagement.recurring.pageTitle")}
				description={t(
					"orgManagement.costManagement.subtitleVariants.recurring",
				)}
				showBackButton={true}
				backButtonText={t("orgManagement.back")}
				hideOnMobile={false}
			/>
			<div className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden">
				<OrganizationManagementNavigation activeTab="cost-management" />
				<div className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto">
					<div className="space-y-4 max-w-6xl mx-auto">
						<CostManagementSubNav orgId={orgId} active="recurring" />

						<Card>
							<CardContent className="p-3 md:p-4 space-y-3">
								<div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
									{next30DaysSummary && (
										<div>
											<p className="text-xs text-base-content/60">
												{t(
													"orgManagement.costManagement.recurring.summary30dLabel",
												)}
											</p>
											<p className="text-lg md:text-xl font-semibold tabular-nums text-base-content">
												{formatCurrency(
													next30DaysSummary.total.toFixed(2),
													next30DaysSummary.currency,
												)}
											</p>
										</div>
									)}
									<Button
										onClick={() => setCreateOpen(true)}
										className="w-full sm:w-auto"
									>
										{t("orgManagement.costManagement.recurring.create")}
									</Button>
								</div>

								{recurringQuery.isLoading && (
									<div className="text-sm text-base-content/60">
										{t("orgManagement.costManagement.loading")}
									</div>
								)}
								{recurringQuery.data && (
									<>
										<div className="md:hidden space-y-2">
											{recurringQuery.data.map((r) => (
												<RecurringMobileCard
													key={r.id}
													item={r}
													categoryName={resolveCategoryName(r.category)}
													nextOccurrenceLabel={
														r.nextOccurrence
															? formatDateString(r.nextOccurrence)
															: "—"
													}
													onEdit={() => setEditTarget(r)}
													onArchive={() =>
														archiveMutation.mutate({
															token,
															organizationId: orgId,
															recurringId: r.id,
														})
													}
													isArchiving={archiveMutation.isPending}
												/>
											))}
											{recurringQuery.data.length === 0 && (
												<div className="text-center py-8 text-sm text-base-content/60">
													{t("orgManagement.costManagement.empty")}
												</div>
											)}
										</div>
										<div className="hidden md:block">
											<Table>
												<TableHeader>
													<TableRow>
														<TableHead>
															{t(
																"orgManagement.costManagement.recurring.columns.description",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.recurring.columns.category",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.recurring.columns.amount",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.recurring.columns.frequency",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.recurring.columns.nextOccurrence",
															)}
														</TableHead>
														<TableHead className="w-12">
															<span className="sr-only">
																{t(
																	"orgManagement.costManagement.recurring.columns.actions",
																)}
															</span>
														</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{recurringQuery.data.map((r) => (
														<TableRow
															key={r.id}
															className="cursor-pointer"
															onClick={() => setEditTarget(r)}
														>
															<TableCell>
																<div className="font-medium">
																	{r.description}
																</div>
																{r.vendor && (
																	<div className="text-xs text-base-content/60">
																		{r.vendor}
																	</div>
																)}
															</TableCell>
															<TableCell>
																<span className="inline-flex items-center gap-2">
																	<span
																		className="inline-block h-3 w-3 rounded-full"
																		style={{
																			backgroundColor: r.category.color,
																		}}
																	/>
																	{resolveCategoryName(r.category)}
																</span>
															</TableCell>
															<TableCell>
																{formatCurrency(r.expectedAmount, r.currency)}
															</TableCell>
															<TableCell>
																{t(
																	`orgManagement.costManagement.recurring.frequency.${r.frequency.toLowerCase()}`,
																)}
															</TableCell>
															<TableCell>
																{r.nextOccurrence
																	? formatDateString(r.nextOccurrence)
																	: "—"}
															</TableCell>
															<TableCell onClick={(ev) => ev.stopPropagation()}>
																<RecurringRowMenu
																	onEdit={() => setEditTarget(r)}
																	onArchive={() =>
																		archiveMutation.mutate({
																			token,
																			organizationId: orgId,
																			recurringId: r.id,
																		})
																	}
																	isArchiving={archiveMutation.isPending}
																/>
															</TableCell>
														</TableRow>
													))}
													{recurringQuery.data.length === 0 && (
														<TableRow>
															<TableCell
																colSpan={6}
																className="text-center py-8 text-base-content/60"
															>
																{t("orgManagement.costManagement.empty")}
															</TableCell>
														</TableRow>
													)}
												</TableBody>
											</Table>
										</div>
									</>
								)}
							</CardContent>
						</Card>
					</div>
				</div>
			</div>

			<RecurringExpenseFormDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				token={token}
				orgId={orgId}
				categories={categoriesQuery.data ?? []}
			/>
			<RecurringExpenseFormDialog
				open={!!editTarget}
				onOpenChange={(open) => {
					if (!open) setEditTarget(undefined);
				}}
				token={token}
				orgId={orgId}
				categories={categoriesQuery.data ?? []}
				template={editTarget}
			/>
		</div>
	);
}

interface RecurringMobileCardProps {
	item: RecurringExpenseDto;
	categoryName: string;
	nextOccurrenceLabel: string;
	onEdit: () => void;
	onArchive: () => void;
	isArchiving: boolean;
}

function RecurringMobileCard({
	item,
	categoryName,
	nextOccurrenceLabel,
	onEdit,
	onArchive,
	isArchiving,
}: RecurringMobileCardProps) {
	const { t } = useTranslation();

	return (
		<button
			type="button"
			onClick={onEdit}
			className="w-full text-left rounded-lg border border-base-300 bg-base-100 p-3 space-y-2 hover:bg-base-200/40 transition-colors"
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<div className="font-medium truncate">{item.description}</div>
					{item.vendor && (
						<div className="text-xs text-base-content/60 truncate">
							{item.vendor}
						</div>
					)}
				</div>
				{/* biome-ignore lint/a11y/noStaticElementInteractions: wrapper exists only to stop click propagation to the parent row button; it is not itself an interactive control */}
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: same as above — no user-facing interaction, only stops propagation */}
				<div onClick={(ev) => ev.stopPropagation()}>
					<RecurringRowMenu
						onEdit={onEdit}
						onArchive={onArchive}
						isArchiving={isArchiving}
					/>
				</div>
			</div>
			<div className="flex items-center justify-between gap-3 text-xs text-base-content/60">
				<span className="inline-flex items-center gap-1.5 truncate min-w-0">
					<span
						className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
						style={{ backgroundColor: item.category.color }}
					/>
					<span className="truncate">{categoryName}</span>
				</span>
				<span className="shrink-0">
					{t(
						`orgManagement.costManagement.recurring.frequency.${item.frequency.toLowerCase()}`,
					)}
				</span>
			</div>
			<div className="flex items-end justify-between gap-3">
				<div>
					<div className="text-[10px] uppercase tracking-wide text-base-content/60">
						{t("orgManagement.costManagement.recurring.columns.amount")}
					</div>
					<div className="text-base font-semibold tabular-nums">
						{formatCurrency(item.expectedAmount, item.currency)}
					</div>
				</div>
				<div className="text-right">
					<div className="text-[10px] uppercase tracking-wide text-base-content/60">
						{t("orgManagement.costManagement.recurring.columns.nextOccurrence")}
					</div>
					<div className="text-sm tabular-nums">{nextOccurrenceLabel}</div>
				</div>
			</div>
		</button>
	);
}

interface RecurringRowMenuProps {
	onEdit: () => void;
	onArchive: () => void;
	isArchiving: boolean;
}

function RecurringRowMenu({
	onEdit,
	onArchive,
	isArchiving,
}: RecurringRowMenuProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	const handleEdit = () => {
		setOpen(false);
		onEdit();
	};
	const handleArchive = () => {
		setOpen(false);
		onArchive();
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className="h-8 w-8 p-0"
					aria-label={t("orgManagement.costManagement.recurring.actions.menu")}
				>
					<MoreHorizontal className="h-4 w-4" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="w-44 p-1"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					type="button"
					className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-base-200"
					onClick={handleEdit}
				>
					{t("orgManagement.costManagement.recurring.actions.edit")}
				</button>
				<button
					type="button"
					className="w-full text-left text-sm px-2 py-1.5 rounded text-error hover:bg-error/10 disabled:opacity-50"
					onClick={handleArchive}
					disabled={isArchiving}
				>
					{t("orgManagement.costManagement.recurring.archive")}
				</button>
			</PopoverContent>
		</Popover>
	);
}

const DAY_MS = 24 * 60 * 60 * 1000;

function projectedNext30DaysTotal(rows: RecurringExpenseDto[]): number {
	const now = Date.now();
	const horizon = now + 30 * DAY_MS;

	return rows.reduce((total, row) => {
		if (row.archivedAt) return total;
		if (!row.nextOccurrence) return total;

		const amount = Number(row.expectedAmount);
		if (!Number.isFinite(amount)) return total;

		const next = new Date(`${row.nextOccurrence}T00:00:00Z`).getTime();
		if (Number.isNaN(next)) return total;

		const occurrences = countOccurrencesInWindow(next, horizon, row.frequency);
		return total + occurrences * amount;
	}, 0);

	function countOccurrencesInWindow(
		start: number,
		end: number,
		frequency: RecurringFrequency,
	): number {
		if (start > end) return 0;
		switch (frequency) {
			case RecurringFrequency.WEEKLY: {
				const ms = end - start;
				return Math.max(0, Math.floor(ms / (7 * DAY_MS)) + 1);
			}
			case RecurringFrequency.MONTHLY:
				return 1; // a single monthly occurrence inside a 30-day window
			case RecurringFrequency.YEARLY:
				return 1;
			default:
				return 1;
		}
	}
}
